import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./intro.css";

type Slide = {
  title?: string;
  eyebrow?: string;
  paragraphs: React.ReactNode[];
  cta?: boolean;
};

type IntroPageProps = {
  onStart: () => void;
};

type IntroCSSVariables = React.CSSProperties & {
  ["--sunset-color"]?: string;
  ["--signal-progress"]?: string;
  ["--sunset-fill"]?: string;
  ["--signal-fill-opacity"]?: string;
  ["--signal-fill-scale"]?: string;
  ["--signal-text-color"]?: string;
  ["--signal-text-fill-color"]?: string;
  ["--ember-fill"]?: string;
};

const slides: Slide[] = [
  {
    title: "High Noon: 반응속도 결투",
    paragraphs: [<span className="scroll-hint">스크롤을 해주세요</span>],
  },
  {
    paragraphs: [
      <>
        시작하면 <span className="sunset-text">석양빛</span>이 짙어지고 사막의 공기가 전해집니다.
      </>,
    ],
  },
  {
    eyebrow: "룰은 간단합니다",
    paragraphs: [
      <>
        <strong className="signal-text">초록 신호</strong>가 켜지는 즉시 클릭하고 <strong>250ms</strong> 안에 고르면 명사수로 인정받습니다.
      </>,
      <>그보다 느리면 조금 더 빠른 손놀림이 필요하겠죠.</>,
    ],
  },
  {
    eyebrow: "시작하면",
    paragraphs: [
      (
        <span className="nowrap">
          시작하면 <span className="ember-text">배경이 붉어지며</span> 사막 위 총잡이의 긴장감이 번집니다.
        </span>
      ),
      <>
        호흡을 고르고 심장 박동에 맞춰
        <br />
        최고의 순간을 노려보세요.
      </>,
    ],
  },
  {
    eyebrow: "준비됐다면",
    paragraphs: [
      <>손목을 가볍게 풀고</>,
      <>
        <strong>게임 시작</strong> 버튼으로 결투에 참전하세요.
      </>,
    ],
    cta: true,
  },
];

const INTRO_BG_URL = `${process.env.PUBLIC_URL}/assets/intro-bg-1.jpg`;

const BASE_TEXT_COLOR = "#1f1812";
const SUNSET_ACCENT_COLOR = "#ff5a3c";

const BASE_SLIDE_DURATION = 2.4;
const HIGHLIGHT_SLIDE_INDEX = 2;
const SUNSET_SLIDE_INDEX = 1;
const EMBER_SLIDE_INDEX = 3;
const PRE_HIGHLIGHT_PAUSE = 0.2;
const HIGHLIGHT_SIGNAL_DURATION = 1.2;
const HIGHLIGHT_HOLD_DURATION = 0.5;
const HIGHLIGHT_RELEASE_DURATION = BASE_SLIDE_DURATION;
const CTA_SLIDE_INDEX = slides.length - 1;
const SUNSET_FILL_DELAY = 0.5;
const SUNSET_FILL_DURATION = 1.15;
const SUNSET_FILL_HOLD = 0.5;
const SUNSET_EXTRA_DURATION = SUNSET_FILL_DELAY + SUNSET_FILL_DURATION + SUNSET_FILL_HOLD;
const SUNSET_SLIDE_DURATION = BASE_SLIDE_DURATION + SUNSET_EXTRA_DURATION;
const EMBER_FILL_DELAY = 0.55;
const EMBER_FILL_DURATION = 1.0;
const EMBER_FILL_HOLD = 0.4;
const EMBER_EXTRA_DURATION = EMBER_FILL_DELAY + EMBER_FILL_DURATION + EMBER_FILL_HOLD;
const EMBER_SLIDE_DURATION = BASE_SLIDE_DURATION + EMBER_EXTRA_DURATION;

const SLIDE_DURATIONS = slides.map((_, idx) => {
  if (idx === CTA_SLIDE_INDEX) return 0;
  if (idx === SUNSET_SLIDE_INDEX) return SUNSET_SLIDE_DURATION;
  if (idx === EMBER_SLIDE_INDEX) return EMBER_SLIDE_DURATION;
  if (idx === HIGHLIGHT_SLIDE_INDEX) {
    return HIGHLIGHT_SIGNAL_DURATION + HIGHLIGHT_HOLD_DURATION + HIGHLIGHT_RELEASE_DURATION;
  }
  return BASE_SLIDE_DURATION;
});

const TOTAL_DURATION = SLIDE_DURATIONS.reduce((sum, len) => sum + len, 0);

const SCROLL_PHASE_FACTOR = 500;
const TOUCH_PHASE_DIVISOR = 400;
const KEY_STEP = 0.8;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const hexToRgb = (hex: string) => {
  const normalized = hex.replace("#", "");
  const bigint = parseInt(normalized, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

const mixColor = (start: string, end: string, t: number) => {
  const a = hexToRgb(start);
  const b = hexToRgb(end);
  const r = Math.round(lerp(a.r, b.r, t));
  const g = Math.round(lerp(a.g, b.g, t));
  const bChannel = Math.round(lerp(a.b, b.b, t));
  return `rgb(${r}, ${g}, ${bChannel})`;
};

const ease = (t: number) => {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
};

type SlideProgress = {
  currentIndex: number;
  rawProgress: number;
  localElapsed: number;
  slideDuration: number;
};

const getSlideProgress = (phaseValue: number): SlideProgress => {
  const clampedPhase = clamp(phaseValue, 0, TOTAL_DURATION);
  let remaining = clampedPhase;

  for (let idx = 0; idx < SLIDE_DURATIONS.length; idx += 1) {
    const length = SLIDE_DURATIONS[idx];

    if (idx === CTA_SLIDE_INDEX) {
      return {
        currentIndex: idx,
        rawProgress: 0,
        localElapsed: 0,
        slideDuration: 0,
      };
    }

    if (remaining <= length || idx === SLIDE_DURATIONS.length - 1) {
      const elapsed = clamp(remaining, 0, length);
      return {
        currentIndex: idx,
        rawProgress: length === 0 ? 0 : elapsed / length,
        localElapsed: elapsed,
        slideDuration: length,
      };
    }

    remaining -= length;
  }

  return {
    currentIndex: CTA_SLIDE_INDEX,
    rawProgress: 0,
    localElapsed: 0,
    slideDuration: 0,
  };
};

export default function IntroPage({ onStart }: IntroPageProps) {
  const [phase, setPhase] = useState(0);
  const phaseRef = useRef(0);
  const touchStartY = useRef<number | null>(null);

  const setPhaseValue = useCallback((value: number) => {
    const next = clamp(value, 0, TOTAL_DURATION);
    phaseRef.current = next;
    setPhase(next);
  }, []);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      setPhaseValue(phaseRef.current + event.deltaY / SCROLL_PHASE_FACTOR);
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown" || event.key === "PageDown") {
        event.preventDefault();
        setPhaseValue(phaseRef.current + KEY_STEP);
      } else if (event.key === "ArrowUp" || event.key === "PageUp") {
        event.preventDefault();
        setPhaseValue(phaseRef.current - KEY_STEP);
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      touchStartY.current = event.touches[0]?.clientY ?? null;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (touchStartY.current === null) return;
      const currentY = event.touches[0]?.clientY ?? touchStartY.current;
      event.preventDefault();
      const delta = touchStartY.current - currentY;
      touchStartY.current = currentY;
      setPhaseValue(phaseRef.current + delta / TOUCH_PHASE_DIVISOR);
    };

    const handleTouchEnd = () => {
      touchStartY.current = null;
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("keydown", handleKey);
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [setPhaseValue]);

  const normalizedPhase = clamp(phase, 0, TOTAL_DURATION);

  const { currentIndex, rawProgress, localElapsed } = useMemo(
    () => getSlideProgress(normalizedPhase),
    [normalizedPhase],
  );

  const nextIndex = Math.min(currentIndex + 1, slides.length - 1);

  const sunsetFillProgress = useMemo(() => {
    if (currentIndex < SUNSET_SLIDE_INDEX) return 0;
    if (currentIndex > SUNSET_SLIDE_INDEX) return 1;
    if (SUNSET_SLIDE_DURATION === 0) return 1;
    if (localElapsed <= SUNSET_FILL_DELAY) return 0;
    if (localElapsed <= SUNSET_FILL_DELAY + SUNSET_FILL_DURATION) {
      return clamp((localElapsed - SUNSET_FILL_DELAY) / SUNSET_FILL_DURATION, 0, 1);
    }
    return 1;
  }, [currentIndex, localElapsed]);

  const tone = normalizedPhase / TOTAL_DURATION < 0.6 ? "day" : "dusk";

  let signalProgress = 0;
  let motionProgress = rawProgress;

  if (currentIndex > HIGHLIGHT_SLIDE_INDEX) {
    signalProgress = 1;
  } else if (currentIndex === HIGHLIGHT_SLIDE_INDEX) {
    const signalEnd = HIGHLIGHT_SIGNAL_DURATION;
    const holdEnd = signalEnd + HIGHLIGHT_HOLD_DURATION;
    signalProgress = clamp(localElapsed / signalEnd, 0, 1);

    if (localElapsed <= holdEnd) {
      motionProgress = 0;
    } else {
      const releaseElapsed = localElapsed - holdEnd;
      motionProgress = clamp(releaseElapsed / HIGHLIGHT_RELEASE_DURATION, 0, 1);
    }
  } else {
    signalProgress = 0;
  }

  if (currentIndex === SUNSET_SLIDE_INDEX) {
    const fillEnd = SUNSET_FILL_DELAY + SUNSET_FILL_DURATION;
    const holdEnd = fillEnd + SUNSET_FILL_HOLD;
    const releaseDuration = BASE_SLIDE_DURATION + SUNSET_FILL_DELAY;
    if (localElapsed <= fillEnd) {
      motionProgress = 0;
    } else if (localElapsed <= holdEnd) {
      motionProgress = 0;
    } else {
      const releaseElapsed = localElapsed - holdEnd;
      motionProgress = clamp(releaseElapsed / releaseDuration, 0, 1);
    }
  } else if (currentIndex === EMBER_SLIDE_INDEX) {
    const fillEnd = EMBER_FILL_DELAY + EMBER_FILL_DURATION;
    const holdEnd = fillEnd + EMBER_FILL_HOLD;
    if (localElapsed <= fillEnd) {
      motionProgress = 0;
    } else if (localElapsed <= holdEnd) {
      motionProgress = 0;
    } else {
      const releaseElapsed = localElapsed - holdEnd;
      motionProgress = clamp(releaseElapsed / BASE_SLIDE_DURATION, 0, 1);
    }
  } else if (currentIndex === HIGHLIGHT_SLIDE_INDEX - 1) {
    motionProgress =
      rawProgress <= PRE_HIGHLIGHT_PAUSE
        ? 0
        : clamp((rawProgress - PRE_HIGHLIGHT_PAUSE) / (1 - PRE_HIGHLIGHT_PAUSE), 0, 1);
  }

  const easedProgress = ease(motionProgress);
  const currentSlide = slides[currentIndex];
  const nextSlide = nextIndex !== currentIndex ? slides[nextIndex] : null;

  const sunsetColor = mixColor(BASE_TEXT_COLOR, SUNSET_ACCENT_COLOR, signalProgress);

  const emberFillProgress = useMemo(() => {
    if (currentIndex < EMBER_SLIDE_INDEX) return 0;
    if (currentIndex > EMBER_SLIDE_INDEX) return 1;
    if (EMBER_SLIDE_DURATION === 0) return 1;
    if (localElapsed <= EMBER_FILL_DELAY) return 0;
    if (localElapsed <= EMBER_FILL_DELAY + EMBER_FILL_DURATION) {
      return clamp((localElapsed - EMBER_FILL_DELAY) / EMBER_FILL_DURATION, 0, 1);
    }
    return 1;
  }, [currentIndex, localElapsed]);

  const signalFillActive = signalProgress > 0 ? 1 : 0;
  const signalFillScale = signalProgress;
  const signalTextBase = BASE_TEXT_COLOR;
  const signalTextFillColor = "#ffffff";

  const { backgroundStyle, backgroundImageStyle } = useMemo(() => {
    const ratio = TOTAL_DURATION ? normalizedPhase / TOTAL_DURATION : 0;
    const clampedRatio = clamp(ratio, 0, 1);
    const fadeProgress = clamp((normalizedPhase - 0.08) / 0.92, 0, 1);
    const baseOpacity = 0;
    const imageOpacity = baseOpacity + (1 - baseOpacity) * fadeProgress;
    const offset = (1 - clampedRatio) * 40;

    const containerStyle: IntroCSSVariables = {
      background: "#ffffff",
      "--sunset-color": sunsetColor,
      "--signal-progress": `${(signalProgress * 100).toFixed(1)}%`,
      "--sunset-fill": sunsetFillProgress.toFixed(3),
      "--signal-fill-opacity": signalFillActive.toFixed(1),
      "--signal-fill-scale": signalFillScale.toFixed(3),
      "--signal-text-color": signalTextBase,
      "--signal-text-fill-color": signalTextFillColor,
      "--ember-fill": emberFillProgress.toFixed(3),
    };

    const imageStyle: React.CSSProperties = {
      opacity: imageOpacity,
      transform: `translateY(${offset}px)`,
      backgroundImage: `url("${INTRO_BG_URL}")`,
      backgroundSize: "clamp(520px, 70vw, 1300px) auto",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center 58%",
    };

    return { backgroundStyle: containerStyle, backgroundImageStyle: imageStyle };
  }, [
    normalizedPhase,
    signalProgress,
    signalFillActive,
    signalFillScale,
    signalTextBase,
    sunsetColor,
    sunsetFillProgress,
    emberFillProgress,
  ]);

  const isHighlightSlide = currentIndex === HIGHLIGHT_SLIDE_INDEX;
  const fadeOutStart = isHighlightSlide ? 0.55 : 0.65;
  const fadeOutStrength = 0.95;
  const fadeOutProgress = clamp((easedProgress - fadeOutStart) / (1 - fadeOutStart), 0, 1);

  const currentStyle: React.CSSProperties = {
    transform: `translateY(${(-90 * easedProgress).toFixed(1)}px)`,
    opacity: 1 - fadeOutProgress * fadeOutStrength,
    filter: `blur(${(fadeOutProgress * 8).toFixed(1)}px)`,
    zIndex: 2,
    pointerEvents: easedProgress > 0.95 ? "none" : "auto",
  };

  const revealStart = isHighlightSlide ? 0.92 : 0.85;
  const revealDuration = isHighlightSlide ? 0.08 : 0.15;
  const revealProgress = clamp((easedProgress - revealStart) / revealDuration, 0, 1);

  const nextStyle: React.CSSProperties | undefined = nextSlide
    ? {
        transform: `translateY(${(120 * (1 - easedProgress)).toFixed(1)}px)`,
        opacity: revealProgress,
        visibility: revealProgress === 0 ? "hidden" : "visible",
        filter: `blur(${((1 - revealProgress) * 8).toFixed(1)}px)`,
        zIndex: 1,
        pointerEvents: easedProgress < 0.95 ? "none" : "auto",
      }
    : undefined;

  const renderContent = (slide: Slide) => (
    <>
      {slide.title && <h1>{slide.title}</h1>}
      <div className="copy">
        {slide.eyebrow && <p className="eyebrow">{slide.eyebrow}</p>}
        {slide.paragraphs.map((paragraph, idx) => (
          <p key={idx} className="body">
            {paragraph}
          </p>
        ))}
      </div>
      {slide.cta && (
        <button type="button" className="start-btn" onClick={onStart} aria-label="게임 시작">
          게임 시작
        </button>
      )}
    </>
  );

  return (
    <div className="intro-root" data-tone={tone} style={backgroundStyle}>
      <div className="intro-bg" style={backgroundImageStyle} aria-hidden="true" />
      <div className="stage" aria-live="polite">
        <div className="slide-stack">
          <article className="slide current" style={currentStyle} aria-live="polite">
            {renderContent(currentSlide)}
          </article>

          {nextSlide && (
            <article className="slide next" style={nextStyle} aria-hidden={revealProgress === 0}>
              {renderContent(nextSlide)}
            </article>
          )}
        </div>
      </div>
    </div>
  );
}
